import React from 'react';
import { Check, X, Zap, Crown, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

interface PlanComparisonProps {
  currentPlan?: 'free' | 'pro' | 'premium';
  onUpgrade?: (plan: 'pro' | 'premium') => void;
  compact?: boolean;
}

interface Feature {
  name: string;
  free: boolean | string;
  pro: boolean | string;
  premium: boolean | string;
  category?: string;
}

const features: Feature[] = [
  // Credits & Benefits
  { name: 'Signup Bonus Credits', free: '50', pro: '100 ($10 value)', premium: '1,000 ($100 value)', category: 'Credits & Benefits' },

  // Core Workflow
  { name: 'Document Viewer', free: true, pro: true, premium: true, category: 'Core Workflow' },
  { name: 'Examination Workflow', free: true, pro: true, premium: true, category: 'Core Workflow' },
  { name: 'Chat with AI Assistant', free: true, pro: true, premium: true, category: 'Core Workflow' },
  { name: 'Draft Generation Tasks', free: true, pro: true, premium: true, category: 'Core Workflow' },
  { name: 'Office Action Analysis', free: true, pro: true, premium: true, category: 'Core Workflow' },
  { name: 'OA Response Shell', free: true, pro: true, premium: true, category: 'Core Workflow' },
  { name: 'Prior Art Summarization', free: true, pro: true, premium: true, category: 'Core Workflow' },

  // Resources & Reference
  { name: 'MPEP Section Navigation', free: true, pro: true, premium: true, category: 'Resources & Reference' },
  { name: 'CFR Section Navigation', free: true, pro: true, premium: true, category: 'Resources & Reference' },
  { name: 'USC Access', free: true, pro: true, premium: true, category: 'Resources & Reference' },
  { name: 'Full Text Search (MPEP/CFR)', free: false, pro: true, premium: true, category: 'Resources & Reference' },
  { name: 'Federal Register Access', free: true, pro: true, premium: true, category: 'Resources & Reference' },
  { name: 'Fee Calculator', free: true, pro: true, premium: true, category: 'Resources & Reference' },
  { name: 'Foreign Patents (EPO) Access', free: false, pro: true, premium: true, category: 'Resources & Reference' },

  // Advanced Features
  { name: 'File Upload for Tasks & Chat', free: false, pro: true, premium: true, category: 'Advanced Features' },
  { name: 'IDS Form Generation', free: '10 credits/form', pro: 'Unlimited', premium: 'Unlimited', category: 'Advanced Features' },
  { name: 'Saved Drafts Library', free: false, pro: false, premium: true, category: 'Advanced Features' },
  { name: 'Translation Tasks', free: false, pro: false, premium: true, category: 'Advanced Features' },
  { name: 'PDF OCR', free: false, pro: false, premium: true, category: 'Advanced Features' },
  { name: 'LaTeX to Word Conversion', free: false, pro: false, premium: true, category: 'Advanced Features' },
  { name: 'Batch Processing', free: false, pro: false, premium: true, category: 'Advanced Features' },

  // Support
  { name: 'Email Support', free: 'Standard', pro: 'Priority', premium: 'Priority', category: 'Support' },
  { name: 'Response Time', free: '48 hours', pro: '24 hours', premium: '12 hours', category: 'Support' },
];

export function PlanComparison({ currentPlan = 'free', onUpgrade, compact = false }: PlanComparisonProps) {
  const plans = [
    {
      id: 'free' as const,
      name: 'Free',
      icon: Sparkles,
      price: '$0',
      period: 'forever',
      color: 'gray',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      buttonColor: 'bg-gray-600',
      description: 'Perfect for getting started',
    },
    {
      id: 'pro' as const,
      name: 'Pro',
      icon: Zap,
      price: '$19',
      period: 'per month',
      color: 'blue',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-300',
      buttonColor: 'bg-blue-600',
      description: 'For serious patent professionals',
      popular: true,
    },
    {
      id: 'premium' as const,
      name: 'Premium',
      icon: Crown,
      price: '$47',
      period: 'per month',
      color: 'purple',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-300',
      buttonColor: 'bg-purple-600',
      description: 'Complete patent workflow solution',
    },
  ];

  const renderFeatureValue = (value: boolean | string) => {
    if (value === true) {
      return <Check className="w-5 h-5 text-green-600 mx-auto" />;
    } else if (value === false) {
      return <X className="w-5 h-5 text-gray-300 mx-auto" />;
    } else {
      return <span className="text-sm text-gray-700 text-center block">{value}</span>;
    }
  };

  const groupedFeatures = features.reduce((acc, feature) => {
    const category = feature.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(feature);
    return acc;
  }, {} as Record<string, Feature[]>);

  if (compact) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="grid grid-cols-3 gap-px bg-gray-200">
          {plans.map((plan) => {
            const PlanIcon = plan.icon;
            const isCurrent = currentPlan === plan.id;

            return (
              <div key={plan.id} className={`${plan.bgColor} p-4 text-center relative`}>
                {plan.popular && !isCurrent && (
                  <Badge className="absolute top-2 right-2 bg-orange-500 text-white text-xs">
                    Popular
                  </Badge>
                )}
                {isCurrent && (
                  <Badge className="absolute top-0 left-1/2 -translate-x-1/2 bg-green-500 text-white text-xs">
                    Current
                  </Badge>
                )}
                <PlanIcon className={`w-8 h-8 text-${plan.color}-600 mx-auto mb-2`} />
                <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
                <div className="mt-2">
                  <span className="text-2xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-sm text-gray-500">/{plan.period.split(' ')[1]}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Choose Your Plan</h2>
        <p className="text-gray-600">Select the perfect plan for your patent workflow needs</p>
      </div>

      {/* Plan Cards */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {plans.map((plan) => {
          const PlanIcon = plan.icon;
          const isCurrent = currentPlan === plan.id;

          return (
            <div
              key={plan.id}
              className={`relative rounded-lg border-2 ${plan.borderColor} ${plan.bgColor} p-6 ${
                plan.popular ? 'shadow-xl scale-105' : 'shadow-md'
              }`}
            >
              {plan.popular && !isCurrent && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-500 text-white">
                  Most Popular
                </Badge>
              )}
              {isCurrent && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-white">
                  Current Plan
                </Badge>
              )}

              <div className="text-center mb-6">
                <PlanIcon className={`w-12 h-12 text-${plan.color}-600 mx-auto mb-3`} />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-sm text-gray-600 mb-4">{plan.description}</p>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-gray-600 ml-2">{plan.period}</span>
                </div>
              </div>

              {!isCurrent && onUpgrade && plan.id !== 'free' && (
                <Button
                  onClick={() => onUpgrade(plan.id)}
                  className={`w-full ${plan.buttonColor} hover:opacity-90 text-white`}
                >
                  Get {plan.name}
                </Button>
              )}
              {isCurrent && (
                <Button disabled className="w-full bg-gray-400 text-white cursor-not-allowed">
                  Current Plan
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Feature Comparison Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-lg">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="py-4 px-6 text-left text-sm font-semibold text-gray-900 w-1/3">
                  Features
                </th>
                {plans.map((plan) => (
                  <th key={plan.id} className="py-4 px-6 text-center text-sm font-semibold text-gray-900">
                    {plan.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedFeatures).map(([category, categoryFeatures]) => (
                <React.Fragment key={category}>
                  {/* Category Header */}
                  <tr className="bg-gray-100">
                    <td colSpan={4} className="py-3 px-6 text-sm font-bold text-gray-700">
                      {category}
                    </td>
                  </tr>
                  {/* Category Features */}
                  {categoryFeatures.map((feature, idx) => (
                    <tr key={feature.name} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="py-3 px-6 text-sm text-gray-700">{feature.name}</td>
                      <td className="py-3 px-6">{renderFeatureValue(feature.free)}</td>
                      <td className="py-3 px-6">{renderFeatureValue(feature.pro)}</td>
                      <td className="py-3 px-6">{renderFeatureValue(feature.premium)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer Note */}
      <div className="mt-6 text-center text-sm text-gray-600">
        <p>All plans include access to core patent analysis features. Upgrade anytime to unlock advanced capabilities.</p>
      </div>
    </div>
  );
}
